import React, { useState, useEffect } from 'react';
import { getQuotes, compareQuotes, selectSupplier, getERPQuotations, createERPQuotation, createDraftEmailFromQuotation } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { formatDate, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';
import { 
  FileText, TrendingUp, DollarSign, Truck, RefreshCw, BarChart, CheckCircle, 
  Plus, X, Receipt, Calendar, User, Mail, Phone, Building, Send, FileEdit
} from 'lucide-react';

function QuoteListPage() {
  const [quotes, setQuotes] = useState([]);
  const [erpQuotations, setErpQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(null);
  const [activeTab, setActiveTab] = useState('erp');
  
  // Form state for new quotation
  const [newQuote, setNewQuote] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    project_name: '',
    items: [{ description: '', unit: 'unit', quantity: 1, unit_price: 0 }],
    validity_days: 30,
    notes: '',
    delivery_time: '4-6 أسابيع',
    payment_terms: '50% مقدم - 50% عند التسليم',
    include_vat: true,
    vat_rate: 15.0
  });

  useEffect(function() { loadAllQuotes(); }, []);

  async function loadAllQuotes() {
    setLoading(true);
    try { 
      const [quotesRes, erpRes] = await Promise.all([
        getQuotes().catch(() => ({ data: [] })),
        getERPQuotations().catch(() => ({ data: { quotations: [] } }))
      ]);
      setQuotes(quotesRes.data || []);
      setErpQuotations(erpRes.data?.quotations || []);
    }
    catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function handleCompare(quote) {
    setSelectedQuote(quote);
    setComparing(true);
    try { const r = await compareQuotes(quote.id); setComparison(r.data); }
    catch(e) { toast.error('فشل المقارنة'); }
    finally { setComparing(false); }
  }

  async function handleSelectSupplier(quoteId, supplierId) {
    try { await selectSupplier(quoteId, supplierId); toast.success('تم اختيار المورد'); loadAllQuotes(); }
    catch(e) { toast.error('فشل'); }
  }
  
  async function handleCreateDraftEmail(quotationId) {
    setCreatingDraft(quotationId);
    try {
      const response = await createDraftEmailFromQuotation(quotationId);
      if (response.data?.success) {
        toast.success('تم إنشاء مسودة البريد بنجاح! يمكنك مراجعتها في قسم المسودات');
      }
    } catch(e) {
      toast.error(e.response?.data?.detail || 'فشل إنشاء مسودة البريد');
    } finally {
      setCreatingDraft(null);
    }
  }
  
  function addItem() {
    setNewQuote(prev => ({
      ...prev,
      items: [...prev.items, { description: '', unit: 'unit', quantity: 1, unit_price: 0 }]
    }));
  }
  
  function removeItem(index) {
    setNewQuote(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  }
  
  function updateItem(index, field, value) {
    setNewQuote(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  }
  
  async function handleCreateQuotation(e) {
    e.preventDefault();
    
    if (!newQuote.client_name || !newQuote.client_email) {
      toast.error('يرجى إدخال اسم العميل والبريد الإلكتروني');
      return;
    }
    
    if (newQuote.items.length === 0 || !newQuote.items[0].description) {
      toast.error('يرجى إضافة بند واحد على الأقل');
      return;
    }
    
    setCreating(true);
    try {
      const response = await createERPQuotation(newQuote);
      if (response.data?.success) {
        toast.success('تم إنشاء عرض السعر بنجاح');
        setShowCreateForm(false);
        setNewQuote({
          client_name: '',
          client_email: '',
          client_phone: '',
          project_name: '',
          items: [{ description: '', unit: 'unit', quantity: 1, unit_price: 0 }],
          validity_days: 30,
          notes: '',
          delivery_time: '4-6 أسابيع',
          payment_terms: '50% مقدم - 50% عند التسليم',
          include_vat: true,
          vat_rate: 15.0
        });
        loadAllQuotes();
      }
    } catch(e) {
      toast.error(e.response?.data?.detail || 'فشل إنشاء عرض السعر');
    } finally {
      setCreating(false);
    }
  }
  
  // Calculate totals
  const calculateSubtotal = () => newQuote.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const calculateVAT = () => newQuote.include_vat ? calculateSubtotal() * (newQuote.vat_rate / 100) : 0;
  const calculateTotal = () => calculateSubtotal() + calculateVAT();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-bold uppercase">عروض الأسعار</h1><p className="text-muted-foreground mt-1">Quotes</p></div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreateForm(true)} data-testid="create-quote-btn">
            <Plus className="h-4 w-4 mr-2" />إنشاء عرض سعر
          </Button>
          <Button onClick={loadAllQuotes} variant="outline" data-testid="refresh-quotes-btn">
            <RefreshCw className={'h-4 w-4 mr-2 ' + (loading ? 'animate-spin' : '')} />تحديث
          </Button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button 
          onClick={() => setActiveTab('erp')}
          className={`px-4 py-2 font-medium transition-colors ${activeTab === 'erp' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}
        >
          عروض الأسعار ({erpQuotations.length})
        </button>
        <button 
          onClick={() => setActiveTab('rfq')}
          className={`px-4 py-2 font-medium transition-colors ${activeTab === 'rfq' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}
        >
          طلبات عروض الأسعار ({quotes.length})
        </button>
      </div>
      
      {/* Create Form Modal */}
      {showCreateForm && (
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-primary" />إنشاء عرض سعر جديد
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowCreateForm(false)}>
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateQuotation} className="space-y-6">
              {/* Client Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <User className="h-4 w-4" />اسم العميل *
                  </label>
                  <Input 
                    value={newQuote.client_name}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, client_name: e.target.value }))}
                    placeholder="شركة النور للمقاولات"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Mail className="h-4 w-4" />البريد الإلكتروني *
                  </label>
                  <Input 
                    type="email"
                    value={newQuote.client_email}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, client_email: e.target.value }))}
                    placeholder="info@company.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Phone className="h-4 w-4" />الهاتف
                  </label>
                  <Input 
                    value={newQuote.client_phone}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, client_phone: e.target.value }))}
                    placeholder="+966 5XX XXX XXXX"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium flex items-center gap-2 mb-2">
                  <Building className="h-4 w-4" />اسم المشروع
                </label>
                <Input 
                  value={newQuote.project_name}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, project_name: e.target.value }))}
                  placeholder="مشروع برج الرياض"
                />
              </div>
              
              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium">البنود</label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-1" />إضافة بند
                  </Button>
                </div>
                <div className="space-y-3">
                  {newQuote.items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start p-3 bg-muted/30 rounded-sm">
                      <div className="flex-1">
                        <Input 
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="وصف البند"
                          className="mb-2"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <Input 
                            value={item.unit}
                            onChange={(e) => updateItem(index, 'unit', e.target.value)}
                            placeholder="الوحدة"
                          />
                          <Input 
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                            placeholder="الكمية"
                            min="0"
                          />
                          <Input 
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            placeholder="سعر الوحدة"
                            min="0"
                          />
                        </div>
                      </div>
                      <div className="text-left min-w-[100px]">
                        <p className="text-xs text-muted-foreground">الإجمالي</p>
                        <p className="font-mono font-medium">{(item.quantity * item.unit_price).toLocaleString()} ر.س</p>
                      </div>
                      {newQuote.items.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)}>
                          <X className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Totals */}
              <div className="bg-muted/50 p-4 rounded-sm space-y-2">
                <div className="flex justify-between">
                  <span>المجموع الفرعي:</span>
                  <span className="font-mono">{calculateSubtotal().toLocaleString()} ر.س</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2">
                    ضريبة القيمة المضافة ({newQuote.vat_rate}%):
                    <input 
                      type="checkbox" 
                      checked={newQuote.include_vat}
                      onChange={(e) => setNewQuote(prev => ({ ...prev, include_vat: e.target.checked }))}
                      className="rounded"
                    />
                  </span>
                  <span className="font-mono">{calculateVAT().toLocaleString()} ر.س</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
                  <span>الإجمالي:</span>
                  <span className="font-mono text-primary">{calculateTotal().toLocaleString()} ر.س</span>
                </div>
              </div>
              
              {/* Terms */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">مدة الصلاحية (أيام)</label>
                  <Input 
                    type="number"
                    value={newQuote.validity_days}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, validity_days: parseInt(e.target.value) || 30 }))}
                    min="1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">مدة التسليم</label>
                  <Input 
                    value={newQuote.delivery_time}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, delivery_time: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">شروط الدفع</label>
                  <Input 
                    value={newQuote.payment_terms}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, payment_terms: e.target.value }))}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">ملاحظات</label>
                <Textarea 
                  value={newQuote.notes}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="ملاحظات إضافية..."
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>إلغاء</Button>
                <Button type="submit" disabled={creating}>
                  {creating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  إنشاء عرض السعر
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* ERP Quotations Tab */}
      {activeTab === 'erp' && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />عروض الأسعار - Contractor Books ({erpQuotations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : erpQuotations.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Receipt className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>لا توجد عروض أسعار في Contractor Books</p>
                <p className="text-sm mt-2">اضغط على "إنشاء عرض سعر" لإضافة عرض جديد</p>
              </div>
            ) : (
              <div className="space-y-4">
                {erpQuotations.map((quote) => (
                  <div 
                    key={quote.id} 
                    className="p-4 rounded-sm bg-muted/30 border border-border hover:border-primary/40"
                    data-testid={'erp-quote-' + quote.id}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="font-mono">{quote.quotation_no || quote.quote_number}</Badge>
                          <Badge className={getStatusColor(quote.status)}>{quote.status}</Badge>
                          {quote.approval_status && (
                            <Badge variant={quote.approval_status === 'approved' ? 'default' : 'secondary'}>
                              {quote.approval_status === 'approved' ? 'معتمد' : quote.approval_status === 'pending' ? 'قيد الانتظار' : quote.approval_status}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-medium text-lg">{quote.customer_name || quote.client?.name}</h3>
                        <p className="text-sm text-muted-foreground">{quote.subject || quote.project_name}</p>
                        {quote.attention_to && (
                          <p className="text-sm text-muted-foreground mt-1">
                            <User className="h-3 w-3 inline mr-1" />{quote.attention_to}
                          </p>
                        )}
                        
                        {/* Items */}
                        <div className="mt-3">
                          <p className="text-xs text-muted-foreground mb-2">البنود ({quote.items?.length || 0})</p>
                          <div className="flex flex-wrap gap-1">
                            {quote.items?.slice(0, 3).map((item, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {item.description?.substring(0, 30)}{item.description?.length > 30 ? '...' : ''} × {item.quantity}
                              </Badge>
                            ))}
                            {quote.items?.length > 3 && (
                              <Badge variant="outline" className="text-xs">+{quote.items.length - 3}</Badge>
                            )}
                          </div>
                        </div>
                        
                        <p className="text-xs text-muted-foreground mt-2">
                          <Calendar className="h-3 w-3 inline mr-1" />
                          صالح حتى: {new Date(quote.valid_date || quote.valid_until).toLocaleDateString('ar-SA')}
                        </p>
                        {quote.user_name && (
                          <p className="text-xs text-muted-foreground mt-1">
                            أنشأه: {quote.user_name}
                          </p>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="mt-3 flex gap-2">
                          {(quote.approval_status === 'approved' || quote.status === 'sent') && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleCreateDraftEmail(quote.id)}
                              disabled={creatingDraft === quote.id}
                              data-testid={'create-draft-btn-' + quote.id}
                            >
                              {creatingDraft === quote.id ? (
                                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <FileEdit className="h-4 w-4 mr-1" />
                              )}
                              إنشاء مسودة بريد
                            </Button>
                          )}
                          {quote.status === 'draft' && (
                            <Badge variant="secondary" className="text-xs">
                              بانتظار الموافقة
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-left">
                        <p className="text-xs text-muted-foreground">الإجمالي</p>
                        <p className="text-2xl font-mono font-bold text-primary">
                          {quote.total?.toLocaleString()} <span className="text-sm">ر.س</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          شامل الضريبة: {quote.vat_amount?.toLocaleString()} ر.س
                        </p>
                        <p className="text-xs text-muted-foreground">
                          ({quote.vat_rate || 15}% VAT)
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* RFQ Tab (Original Quotes) */}
      {activeTab === 'rfq' && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />طلبات عروض الأسعار ({quotes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : quotes.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>لا توجد طلبات عروض أسعار</p>
              </div>
            ) : (
              <div className="space-y-4">
                {quotes.map((quote) => (
                  <div 
                    key={quote.id} 
                    className="p-4 rounded-sm bg-muted/30 border border-border hover:border-primary/40" 
                    data-testid={'quote-item-' + quote.id}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{quote.client_name}</h3>
                          <Badge variant="outline" className={getStatusColor(quote.status)}>{quote.status}</Badge>
                        </div>
                        {quote.project_name && <p className="text-sm text-muted-foreground mt-1">المشروع: {quote.project_name}</p>}
                        <p className="text-xs text-muted-foreground mt-1 font-mono">{formatDate(quote.created_at)}</p>
                        {quote.items && quote.items.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-muted-foreground mb-2">المواد ({quote.items.length})</p>
                            <div className="flex flex-wrap gap-1">
                              {quote.items.slice(0, 3).map((item, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">{item.description}</Badge>
                              ))}
                              {quote.items.length > 3 && <Badge variant="outline" className="text-xs">+{quote.items.length - 3}</Badge>}
                            </div>
                          </div>
                        )}
                        {quote.supplier_quotes && quote.supplier_quotes.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-muted-foreground mb-2">عروض الموردين ({quote.supplier_quotes.length})</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {quote.supplier_quotes.map((sq, idx) => (
                                <div 
                                  key={idx} 
                                  className={`p-2 rounded-sm border text-xs ${quote.selected_supplier_id === sq.supplier_id ? 'bg-green-500/10 border-green-500/30' : 'bg-muted/20 border-border'}`}
                                >
                                  <p className="font-medium truncate">{sq.supplier_name}</p>
                                  <div className="flex items-center gap-1 mt-1 text-muted-foreground">
                                    <DollarSign className="h-3 w-3" /><span className="font-mono">{sq.total_price}</span>
                                  </div>
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <Truck className="h-3 w-3" /><span>{sq.delivery_time}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        {quote.supplier_quotes && quote.supplier_quotes.length > 1 && (
                          <Button size="sm" variant="outline" onClick={() => handleCompare(quote)} data-testid={'compare-btn-' + quote.id}>
                            <BarChart className="h-4 w-4 mr-1" />مقارنة
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Comparison Modal */}
      {selectedQuote && comparison && (
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />مقارنة العروض
            </CardTitle>
            <Button variant="ghost" onClick={() => { setSelectedQuote(null); setComparison(null); }}>إغلاق</Button>
          </CardHeader>
          <CardContent>
            {comparing ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 rounded-sm bg-primary/10 border border-primary/20">
                  <h4 className="font-medium text-primary mb-2">التوصية</h4>
                  <p className="text-sm">{comparison.recommendation}</p>
                  <p className="text-sm text-muted-foreground mt-1" dir="rtl">{comparison.recommendation_ar}</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 rounded-sm bg-green-500/10 border border-green-500/20">
                    <p className="text-xs text-muted-foreground">أفضل قيمة</p>
                    <p className="font-medium text-green-500">{comparison.best_value}</p>
                  </div>
                  <div className="p-3 rounded-sm bg-blue-500/10 border border-blue-500/20">
                    <p className="text-xs text-muted-foreground">أسرع توريد</p>
                    <p className="font-medium text-blue-500">{comparison.fastest_delivery}</p>
                  </div>
                  <div className="p-3 rounded-sm bg-yellow-500/10 border border-yellow-500/20">
                    <p className="text-xs text-muted-foreground">أقل سعر</p>
                    <p className="font-medium text-yellow-500">{comparison.lowest_price}</p>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  {selectedQuote.supplier_quotes?.map((sq) => (
                    <Button 
                      key={sq.supplier_id} 
                      variant={sq.supplier_name === comparison.best_value ? 'default' : 'outline'} 
                      onClick={() => handleSelectSupplier(selectedQuote.id, sq.supplier_id)} 
                      data-testid={'select-supplier-' + sq.supplier_id}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />اختيار {sq.supplier_name}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default QuoteListPage;
